import React, { Suspense, lazy } from 'react';
import moduleRegistry from './moduleRegistry';
import ComponentErrorBoundary from '../components/ComponentErrorBoundary';

// Component loading cache
const componentCache = new Map();

// Component loader configurations
const componentLoaders = {

  // Task Management Component
  'TasksPage': () => lazy(() => import('../pages/TasksPage')),

  // Productivity Coaching Components
  'ProductivityCoachingDashboard': () => lazy(() => import('../pages/ProductivityCoachingDashboard')),
  'Agents': () => lazy(() => import('../pages/Agents')),
  'AgentDetail': () => lazy(() => import('../pages/AgentDetail')),
  'AccountabilityGrid': () => lazy(() => import('../pages/AccountabilityGrid')),
  'Pipeline': () => lazy(() => import('../pages/Pipeline')),
  'PipelineDetail': () => lazy(() => import('../pages/PipelineDetail')),
  'Communications': () => lazy(() => import('../pages/Communications')),
  'CommunicationDetail': () => lazy(() => import('../pages/CommunicationDetail')),
  'ClassSchedule': () => lazy(() => import('../pages/ClassSchedule')),
  'InstructorDirectoryAdmin': () => lazy(() => import('../pages/InstructorDirectoryAdmin')),
  'Classes': () => lazy(() => import('../pages/Classes')),
  'ClassEventDetail': () => lazy(() => import('../pages/ClassEventDetail')),
  'InstructorDetail': () => lazy(() => import('../pages/InstructorDetail')),

  
  // Add missing components that might be referenced
  'LaunchSeries': () => lazy(() => import('../pages/LaunchSeries')),
  
  // Task Planning Components
  'TaskPlansPage': () => lazy(() => import('../pages/TaskPlansPage')),
  'CreateTaskPlan': () => lazy(() => import('../pages/CreateTaskPlan')),
  'EditTaskPlan': () => lazy(() => import('../pages/EditTaskPlan')),
  'TaskPlanDetails': () => lazy(() => import('../pages/TaskPlanDetails')),
  'PlanAssignments': () => lazy(() => import('../pages/PlanAssignments')),
  'TaskAnalyticsDashboard': () => lazy(() => import('../pages/TaskAnalyticsDashboard')),

  // Plans Page (combined Task Plans + Learning Paths)
  'PlansPage': () => lazy(() => import('../pages/PlansPage')),

  // Learning Paths Components
  'CreateLearningPath': () => lazy(() => import('../pages/CreateLearningPath')),
  'EditLearningPath': () => lazy(() => import('../pages/EditLearningPath')),
  'LearningPathDetails': () => lazy(() => import('../pages/LearningPathDetails')),
  'AgentLearningProgress': () => lazy(() => import('../pages/AgentLearningProgress')),
  'EvidenceReviewQueue': () => lazy(() => import('../pages/EvidenceReviewQueue')),

  // Agent Resources Components
  'ResourcesPage': () => lazy(() => import('../pages/ResourcesPage')),
  'ResourceDetailPage': () => lazy(() => import('../pages/ResourceDetailPage')),

  // Referrals Component
  'Referrals': () => lazy(() => import('../pages/Referrals')),

  // Super Admin components
  'SuperAdminDashboard': () => lazy(() => import('../pages/SuperAdminDashboard')),
  'SuperAdminOverview': () => lazy(() => import('../pages/SuperAdminOverview')),
  'SuperAdminOrganizations': () => lazy(() => import('../pages/SuperAdminOrganizations')),
  'SuperAdminOrganizationDetail': () => lazy(() => import('../pages/SuperAdminOrganizationDetail')),
  'SuperAdminUsers': () => lazy(() => import('../pages/SuperAdminUsers')),
  'SuperAdminUserDetail': () => lazy(() => import('../pages/SuperAdminUserDetail')),
  'SuperAdminUsage': () => lazy(() => import('../pages/SuperAdminUsage'))
};

class ModuleLoader {
  constructor() {
    this.loadingStates = new Map();
    this.errorStates = new Map();
  }

  /**
   * Load a component dynamically with caching
   */
  async loadComponent(componentName) {
    if (componentCache.has(componentName)) {
      return componentCache.get(componentName);
    }

    if (!componentLoaders[componentName]) {
      throw new Error(`Component loader not found: ${componentName}`);
    }

    try {
      this.loadingStates.set(componentName, true);
      const loader = componentLoaders[componentName];
      
      // Get the lazy component - this returns a React.lazy() wrapped component
      const LazyComponent = loader();
      
      // For caching purposes, we store the lazy component itself
      componentCache.set(componentName, LazyComponent);
      this.loadingStates.set(componentName, false);
      this.errorStates.delete(componentName);
      
      return LazyComponent;
    } catch (error) {
      this.loadingStates.set(componentName, false);
      this.errorStates.set(componentName, error);
      console.error(`Error loading component ${componentName}:`, error);
      throw error;
    }
  }

  /**
   * Create a wrapped component with loading and error handling
   */
  createWrappedComponent(componentName, moduleId) {
    const LoadingComponent = () => (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading {componentName}...</span>
      </div>
    );

    const ErrorComponent = ({ error }) => (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Failed to Load Component
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Could not load {componentName} from {moduleId} module
        </p>
        <p className="text-xs text-red-600 mb-4">
          {error?.message || 'Unknown error'}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          Reload Page
        </button>
      </div>
    );

    // Check if loader exists
    if (!componentLoaders[componentName]) {
      return () => <ErrorComponent error={{ message: `No loader found for component: ${componentName}` }} />;
    }

    // Get the lazy component directly from the loader
    const loader = componentLoaders[componentName];
    const LazyComponent = loader();

    // Wrap with error boundary and suspense
    const WrappedComponent = (props) => (
      <ComponentErrorBoundary>
        <Suspense fallback={<LoadingComponent />}>
          <LazyComponent {...props} />
        </Suspense>
      </ComponentErrorBoundary>
    );

    return WrappedComponent;
  }

  /**
   * Get all loadable routes from active modules
   */
  getModuleRoutes() {
    moduleRegistry.init();
    const routes = moduleRegistry.getModuleRoutes();
    
    return routes.map(route => ({
      ...route,
      component: typeof route.component === 'string' 
        ? this.createWrappedComponent(route.component, route.moduleId)
        : route.component
    }));
  }

  /**
   * Preload critical components
   */
  async preloadCriticalComponents() {
    const criticalComponents = [
      'GPSDashboard',
      'ProductivityCoachingDashboard',
      'CurrentGoal',
      'AllGoals'
    ];

    const loadPromises = criticalComponents.map(async (componentName) => {
      try {
        await this.loadComponent(componentName);
        console.log(`Preloaded component: ${componentName}`);
      } catch (error) {
        console.warn(`Failed to preload component ${componentName}:`, error);
      }
    });

    await Promise.allSettled(loadPromises);
  }

  /**
   * Check if component is loading
   */
  isComponentLoading(componentName) {
    return this.loadingStates.get(componentName) || false;
  }

  /**
   * Get component loading error
   */
  getComponentError(componentName) {
    return this.errorStates.get(componentName);
  }

  /**
   * Clear component cache (useful for development/hot reload)
   */
  clearCache(componentName) {
    if (componentName) {
      componentCache.delete(componentName);
      this.loadingStates.delete(componentName);  
      this.errorStates.delete(componentName);
    } else {
      componentCache.clear();
      this.loadingStates.clear();
      this.errorStates.clear();
    }
  }

  /**
   * Force reload a component by clearing its cache and re-importing
   */
  async forceReloadComponent(componentName) {
    // Clear from cache
    this.clearCache(componentName);
    
    // Force browser to reload the module
    if (componentLoaders[componentName]) {
      try {
        const loader = componentLoaders[componentName];
        const lazyComponent = loader();
        const Component = await lazyComponent;
        componentCache.set(componentName, Component);
        return Component;
      } catch (error) {
        console.error(`Force reload failed for ${componentName}:`, error);
        throw error;
      }
    }
    throw new Error(`Component loader not found: ${componentName}`);
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      cachedComponents: componentCache.size,
      loadingComponents: Array.from(this.loadingStates.values()).filter(Boolean).length,
      errorComponents: this.errorStates.size,
      totalRegistered: Object.keys(componentLoaders).length
    };
  }

  /**
   * Register a new component loader dynamically
   */
  registerComponentLoader(componentName, loader) {
    if (typeof loader !== 'function') {
      throw new Error('Component loader must be a function');
    }
    
    componentLoaders[componentName] = loader;
    console.log(`Registered component loader: ${componentName}`);
  }

  /**
   * Load module-specific assets (CSS, config, etc.)
   */
  async loadModuleAssets(moduleId) {
    const module = moduleRegistry.getModule(moduleId);
    if (!module) {
      throw new Error(`Module not found: ${moduleId}`);
    }

    const assets = [];

    // Load module-specific CSS if it exists
    try {
      const cssPath = `/modules/${moduleId}/styles.css`;
      const cssResponse = await fetch(cssPath);
      if (cssResponse.ok) {
        const cssText = await cssResponse.text();
        const style = document.createElement('style');
        style.textContent = cssText;
        style.setAttribute('data-module', moduleId);
        document.head.appendChild(style);
        assets.push('styles.css');
      }
    } catch (error) {
      // CSS is optional, don't throw
      console.debug(`No CSS found for module ${moduleId}`);
    }

    // Load module configuration
    try {
      const configPath = `/modules/${moduleId}/config.json`;
      const configResponse = await fetch(configPath);
      if (configResponse.ok) {
        const config = await configResponse.json();
        // Merge with existing module settings
        const currentSettings = moduleRegistry.getModuleSettings(moduleId);
        moduleRegistry.updateModuleSettings(moduleId, { ...currentSettings, ...config });
        assets.push('config.json');
      }
    } catch (error) {
      console.debug(`No config found for module ${moduleId}`);
    }

    return assets;
  }

  /**
   * Unload module assets
   */
  unloadModuleAssets(moduleId) {
    // Remove module-specific CSS
    const styles = document.querySelectorAll(`style[data-module="${moduleId}"]`);
    styles.forEach(style => style.remove());

    // Clear module components from cache
    const moduleRoutes = moduleRegistry.getModule(moduleId)?.routes || [];
    moduleRoutes.forEach(route => {
      if (typeof route.component === 'string') {
        this.clearCache(route.component);
      }
    });
  }

  /**
   * Initialize the module loader
   */
  async init() {
    moduleRegistry.init();
    
    // Clear any existing cache to ensure fresh start
    this.clearCache();
    
    // Preload critical components in the background
    setTimeout(() => {
      this.preloadCriticalComponents().catch(console.error);
    }, 1000);

    // Load assets for active modules
    const activeModules = moduleRegistry.getActiveModules();
    for (const module of activeModules) {
      try {
        await this.loadModuleAssets(module.id);
      } catch (error) {
        console.warn(`Failed to load assets for module ${module.id}:`, error);
      }
    }

    console.log('Module loader initialized');
  }
}

// Create singleton instance
const moduleLoader = new ModuleLoader();

export default moduleLoader;
